import {put, call, take} from "typed-redux-saga";
import {
	fetchLeaguesSuccess,
	fetchLeaguesFailed,
	IFetchLeaguesPayload,
	showGlobalError,
	ICreateLeaguePayload,
	createLeagueSuccess,
	createLeagueFailed,
	IInvitePayload,
	inviteToLeagueSuccess,
	inviteToLeagueFailed,
	setLeagueCreated,
	IFetchLeaguesForJoinPayload,
	fetchLeaguesForJoinSuccess,
	fetchLeaguesForJoinFailed,
	IJoinLeaguePayload,
	joinLeagueSuccess,
	joinLeagueFailed,
	setLeagueJoined,
	fetchMoreLeaguesForJoin,
	ILeaveLeaguePayload,
	leaveLeagueSuccess,
	leaveLeagueFailed,
	ILeagueRankingsPayload,
	fetchLeagueRankingsSuccess,
	fetchLeagueRankingsFailed,
	fetchMoreLeagueRankings,
	ILeagueUsersPayload,
	fetchLeagueUsersSuccess,
	fetchLeagueUsersFailed,
	IRemoveUserPayload,
	removeUserSuccess,
	removeUserFailed,
	openInvitesSentModal,
	IUpdateLeaguePayload,
	updateLeagueSuccess,
	updateLeagueFailed,
	closeRemoveUserModal,
	closeLeaveLeagueModal,
	fetchMoreLeagueUsers,
	IFetchLeagueForJoinPayload,
	fetchLeagueForJoinSuccess,
	fetchLeagueForJoinFailed,
	openErrorLeagueModal,
	fetchGotcLeagueSuccess,
	fetchGotcLeagueError,
} from "modules/actions";
import {Api, ApiError} from "modules/utils";
import {ISagaAction} from "modules/types";
import {SagaIterator} from "redux-saga";

export const fetchLeaguesSaga = function* ({payload}: ISagaAction<IFetchLeaguesPayload>) {
	try {
		const result = yield* call(Api.Leagues.leagues, payload);
		yield* put(fetchLeaguesSuccess(result.success.leagues));
	} catch (err) {
		const {message} = err as Error;
		yield* put(fetchLeaguesFailed(message));
		yield* put(showGlobalError(err as Error));
	}
};

export const createLeagueSaga = function* ({payload}: ISagaAction<ICreateLeaguePayload>) {
	try {
		const result = yield* call(Api.Leagues.league, payload);
		yield* put(createLeagueSuccess(result.success));
		yield* put(setLeagueCreated());
	} catch (err) {
		const {message, code} = err as ApiError;
		yield* put(createLeagueFailed(message));

		if (code === 423) {
			// team has not been created
			yield* put(openErrorLeagueModal(err as Error));
		} else {
			yield* put(showGlobalError(err as Error));
		}
	}
};

export const updateLeagueSaga = function* ({payload}: ISagaAction<IUpdateLeaguePayload>) {
	try {
		const result = yield* call(Api.Leagues.update, payload);
		yield* put(updateLeagueSuccess(result.success));
	} catch (err) {
		const {message} = err as Error;
		yield* put(updateLeagueFailed(message));
		yield* put(showGlobalError(err as Error));
	}
};

export const inviteToLeagueSaga = function* ({payload}: ISagaAction<IInvitePayload>) {
	try {
		yield* call(Api.Leagues.invite, payload);
		yield* put(inviteToLeagueSuccess());
		yield* put(openInvitesSentModal());
	} catch (err) {
		const {message} = err as Error;
		yield* put(inviteToLeagueFailed(message));
		yield* put(showGlobalError(err as Error));
	}
};

export const fetchLeaguesForJoinSaga = function* (
	action: ISagaAction<IFetchLeaguesForJoinPayload>
): SagaIterator {
	try {
		const {payload} = action;
		const result = yield* call(Api.Leagues.showForJoin, payload);
		yield* put(fetchLeaguesForJoinSuccess(result.success));

		/**
		 * Load more actions
		 */
		if (result.success.nextPage) {
			const {page} = payload;
			const nextPage = page ? page + 1 : 2;

			yield* take(fetchMoreLeaguesForJoin);
			yield* call(fetchLeaguesForJoinSaga, {
				...action,
				payload: {
					...payload,
					page: nextPage,
				},
			});
		}
	} catch (err) {
		const {message} = err as Error;
		yield* put(fetchLeaguesForJoinFailed(message));
		yield* put(showGlobalError(err as Error));
	}
};

export const joinLeagueSaga = function* ({payload}: ISagaAction<IJoinLeaguePayload>) {
	try {
		const result = yield* call(Api.Leagues.join, payload);
		yield* put(joinLeagueSuccess(result.success));
		yield* put(setLeagueJoined(result.success.id));
	} catch (err) {
		const {message} = err as Error;
		yield* put(joinLeagueFailed(message));
		yield* put(showGlobalError(err as Error));
	}
};

export const leaveLeagueSaga = function* ({payload}: ISagaAction<ILeaveLeaguePayload>) {
	try {
		yield* call(Api.Leagues.leave, payload);
		yield* put(leaveLeagueSuccess(payload.leagueId));
		yield* put(closeLeaveLeagueModal());
	} catch (err) {
		const {message} = err as Error;
		yield* put(leaveLeagueFailed(message));
		yield* put(showGlobalError(err as Error));
	}
};

export const fetchLeagueRankingsSaga = function* (
	action: ISagaAction<ILeagueRankingsPayload>
): SagaIterator {
	try {
		const {payload} = action;
		const result = yield* call(Api.Leagues.ranking, payload);
		yield* put(fetchLeagueRankingsSuccess(result.success));

		/**
		 * Load more actions
		 */
		if (result.success.nextPage) {
			const {page} = payload;
			const nextPage = page ? page + 1 : 2;

			yield* take(fetchMoreLeagueRankings);
			yield* call(fetchLeagueRankingsSaga, {
				...action,
				payload: {
					...payload,
					page: nextPage,
				},
			});
		}
	} catch (err) {
		const {message} = err as Error;
		yield* put(fetchLeagueRankingsFailed(message));
		yield* put(showGlobalError(err as Error));
	}
};

export const fetchLeagueUsersSaga = function* (
	action: ISagaAction<ILeagueUsersPayload>
): SagaIterator {
	try {
		const {payload} = action;
		const result = yield* call(Api.Leagues.users, payload);
		yield* put(fetchLeagueUsersSuccess(result.success));

		/**
		 * Load more actions
		 */
		if (result.success.nextPage) {
			const {page} = payload;
			const nextPage = page ? page + 1 : 2;

			yield* take(fetchMoreLeagueUsers);
			yield* call(fetchLeagueUsersSaga, {
				...action,
				payload: {
					...payload,
					page: nextPage,
				},
			});
		}
	} catch (err) {
		const {message} = err as Error;
		yield* put(fetchLeagueUsersFailed(message));
		yield* put(showGlobalError(err as Error));
	}
};

export const removeUserSaga = function* ({payload}: ISagaAction<IRemoveUserPayload>) {
	try {
		yield* call(Api.Leagues.remove, payload);
		yield* put(removeUserSuccess(payload.userId));
		yield* put(closeRemoveUserModal());
	} catch (err) {
		const {message} = err as Error;
		yield* put(removeUserFailed(message));
		yield* put(showGlobalError(err as Error));
	}
};

export const fetchLeagueForJoinSaga = function* ({
	payload,
}: ISagaAction<IFetchLeagueForJoinPayload>) {
	try {
		const result = yield* call(Api.Leagues.showByCode, payload);
		yield* put(fetchLeagueForJoinSuccess(result.success));
	} catch (err) {
		const {message} = err as Error;
		yield* put(fetchLeagueForJoinFailed(message));
		yield* put(showGlobalError(err as Error));
	}
};

export const fetchGotcLeagueSaga = function* () {
	try {
		const result = yield* call(Api.Leagues.gotcLeague);
		yield* put(fetchGotcLeagueSuccess(result.success.leagues));
	} catch (err) {
		const {message} = err as Error;
		yield* put(fetchGotcLeagueError(message));
		yield* put(showGlobalError(err as Error));
	}
};
